import React, { KeyboardEvent, FC } from 'react';
import { ApolloConsumer } from '@apollo/react-hooks';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { InputAutoSuggestWithLabel } from '@blueprint/text-input';
import { useDispatch, useSelector } from 'react-redux';
import { getTranslator } from '../../i18n/getTranslator';
import { GEO_LOCATIONS_QUERY } from '../../../queries/search/geo-locations';
import ADDRESS_POSTCODE_SUGGESTION_QUERY from 'queries/search/addressPostcode.graphql';
import searchTranslations from 'public/locales/en/search.json';
import { updateFilter, locationTextSelector } from 'reducers/filters';
import { Tab } from 'homepage/components/HeroContainer/HeroContainer.types';
import {
    transformGeoLocationData,
    transformAddressPostcodeSuggestion
} from './transforms';

type LocationSearchInputProps = {
    id?: string;
    onLocationInputKeyDown: () => void;
    onInputTextChange?: (value: string) => void;
    hasExtendedHeight?: boolean;
    hasBorderRight?: boolean;
    onlyDisplayRemoveIconWhenActive?: boolean;
    searchType?: Tab;
    value: string;
};

type LocationType = {
    id: string;
    value: string | number | string[];
};

const t = getTranslator(searchTranslations);

const inputPlaceholder = t('filters.field.q.placeholder');

export const LocationSearchInput: FC<LocationSearchInputProps> = ({
    id,
    onLocationInputKeyDown,
    hasExtendedHeight,
    hasBorderRight,
    onlyDisplayRemoveIconWhenActive,
    searchType,
    value
}) => {
    const reduxDispatch = useDispatch();

    const { selectedValue: locationTextValue } = useSelector(
        locationTextSelector
    );

    const onLocationSelect = (location: LocationType) => {
        reduxDispatch(updateFilter('q', location.value as string));
        reduxDispatch(updateFilter('identifier', location.id));
        reduxDispatch(updateFilter('locationText', ''));
    };

    const handleInputTextChange = (value: string) => {
        reduxDispatch(updateFilter('locationText', value));
    };

    const handleInputKeyDown = (evt: KeyboardEvent) => {
        evt.preventDefault();

        if (evt.key === 'Enter') {
            onLocationInputKeyDown();
        }
    };

    const isHousePricesSearch = searchType === 'house-prices';

    return (
        <>
            <ApolloConsumer>
                {(client) => {
                    const locationRegex = /([A-Za-zÀ-ÖØ-öø-ÿ])/;
                    const housePricesRegex = /([0-9A-Za-zÀ-ÖØ-öø-ÿ])/;

                    const getGeoLocationData = async (value: string) => {
                        const { data } = await client.query({
                            query: GEO_LOCATIONS_QUERY,
                            variables: { locationPrefix: value }
                        });

                        return transformGeoLocationData(data.geoSuggestion);
                    };

                    const getAddressPostcodeSuggestion = async (
                        value: string
                    ) => {
                        const { data } = await client.query({
                            query: ADDRESS_POSTCODE_SUGGESTION_QUERY,
                            variables: { addressPartial: value }
                        });
                        const locationTextValueSplit = locationTextValue.split(
                            ' '
                        );
                        const firstElement = locationTextValueSplit[0];
                        const isFirstElementANumber = !isNaN(
                            parseInt(firstElement, 10)
                        );

                        const prefixElement = isFirstElementANumber
                            ? firstElement
                            : '';

                        return transformAddressPostcodeSuggestion(
                            data.getSuggestedAddresses,
                            prefixElement
                        );
                    };

                    const getSuggestionFunction = isHousePricesSearch
                        ? getAddressPostcodeSuggestion
                        : getGeoLocationData;

                    const getSuggestionFunctionDebounced = AwesomeDebouncePromise(
                        getSuggestionFunction,
                        300,
                        { onlyResolvesLast: true }
                    );

                    return (
                        <InputAutoSuggestWithLabel
                            getData={getSuggestionFunctionDebounced}
                            suggestionSelectedAction={onLocationSelect}
                            placeholder={inputPlaceholder}
                            validInputRegex={
                                isHousePricesSearch
                                    ? housePricesRegex
                                    : locationRegex
                            }
                            id={id}
                            value={value}
                            autoComplete="off"
                            inputOnKeyDown={handleInputKeyDown}
                            inputTextChange={handleInputTextChange}
                            hasExtendedHeight={hasExtendedHeight}
                            hasBorderRight={hasBorderRight}
                            onlyDisplayRemoveIconWhenActive={
                                onlyDisplayRemoveIconWhenActive
                            }
                        />
                    );
                }}
            </ApolloConsumer>
        </>
    );
};
